import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Building2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { api, formatApiError } from "../lib/api";

const CANDIDATE_PATHS = ["/", "/candidates", "/cv-review"];
const EMPLOYER_PATHS = ["/employers"];
const inputCls = "w-full rounded-lg border border-[#e3e0d3] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";

const VARIANTS = {
  talent: {
    storageKey: "ditch_talent_popup",
    tag: "Candidate",
    kicker: "Before you go",
    icon: Mail,
    headline: "Get the jobs",
    accent: "before they're advertised.",
    body: "Market updates, salary insights and roles across every major Australian city — straight from our recruiters. No spam, unsubscribe anytime.",
    cta: "Keep me posted",
    thanksTitle: "You're on the list — thanks!",
    thanksBody: "Keep an eye on your inbox for market updates and roles worth knowing about.",
    company: false,
  },
  employer: {
    storageKey: "ditch_employer_popup",
    tag: "Employer Prospect",
    kicker: "Hiring soon?",
    icon: Building2,
    headline: "Skip the job boards.",
    accent: "Let's talk hiring.",
    body: "Tell us who you are and one of our Australian recruiters will reach out with how membership works, pricing, and what a shortlist looks like.",
    cta: "Get in touch",
    thanksTitle: "Thanks — we'll be in touch!",
    thanksBody: "One of our recruiters will reach out within 1 business day to talk through your hiring needs.",
    company: true,
  },
};

export const LeadCaptureDialog = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company_name: "", website: "" });

  const isEmployer = EMPLOYER_PATHS.includes(pathname);
  const isCandidate = CANDIDATE_PATHS.includes(pathname) || pathname.startsWith("/recruiting/");
  const variantKey = isEmployer ? "employer" : isCandidate ? "talent" : null;
  const v = variantKey ? VARIANTS[variantKey] : null;

  useEffect(() => {
    setOpen(false);
    setDone(false);
    if (!v || localStorage.getItem(v.storageKey)) return;
    let fired = false;
    const cleanups = [];
    const trigger = () => {
      if (fired) return;
      fired = true;
      setOpen(true);
      cleanups.forEach((fn) => fn());
    };
    const onExitIntent = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };
    document.addEventListener("mouseout", onExitIntent);
    cleanups.push(() => document.removeEventListener("mouseout", onExitIntent));
    if (variantKey === "employer") {
      const t = setTimeout(trigger, 15000);
      cleanups.push(() => clearTimeout(t));
    } else {
      const onScroll = () => {
        const doc = document.documentElement;
        const max = doc.scrollHeight - window.innerHeight;
        if (max > 0 && window.scrollY / max >= 0.5) trigger();
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener("scroll", onScroll));
    }
    return () => cleanups.forEach((fn) => fn());
  }, [pathname, variantKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = (o) => {
    setOpen(o);
    if (!o && !done && v) localStorage.setItem(v.storageKey, "dismissed");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/marketing/lead", {
        name: form.name,
        email: form.email,
        company_name: v.company ? form.company_name : "",
        website: form.website,
        tag: v.tag,
        source: variantKey === "employer" ? "employer_popup" : "talent_popup",
      });
      localStorage.setItem(v.storageKey, "subscribed");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!v) return null;
  const Icon = v.icon;
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#fcfbf8] max-w-md" data-testid={`lead-capture-dialog-${variantKey}`}>
        {done ? (
          <div className="py-6 text-center" data-testid="lead-capture-thanks">
            <CheckCircle2 size={40} className="mx-auto text-[#7C8514]" />
            <h3 className="font-heading font-bold text-[#0d2f4d] text-xl mt-4">{v.thanksTitle}</h3>
            <p className="text-sm text-[#51637a] mt-2 leading-relaxed">{v.thanksBody}</p>
            <button onClick={() => setOpen(false)} data-testid="lead-capture-close"
              className="mt-5 rounded-full bg-[#0d2f4d] text-white text-sm font-medium px-8 py-3 hover:bg-[#1c4d78] transition-colors">
              Close
            </button>
          </div>
        ) : (
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-heading font-bold text-[#7C8514]">
              <Icon size={14} /> {v.kicker}
            </div>
            <h3 className="font-heading font-black uppercase text-[#0d2f4d] text-2xl tracking-tight mt-2 leading-tight">
              {v.headline}<br /><span className="font-accent font-medium normal-case italic text-[#7C8514]">{v.accent}</span>
            </h3>
            <p className="text-sm text-[#51637a] mt-2 leading-relaxed">{v.body}</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} />
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name" data-testid="lead-name-input" className={inputCls} />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email address" data-testid="lead-email-input" className={inputCls} />
              {v.company && (
                <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Company name" data-testid="lead-company-input" className={inputCls} />
              )}
              <button type="submit" disabled={busy} data-testid="lead-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-semibold text-sm py-3.5 hover:bg-[#aeb81f] transition-colors disabled:opacity-60">
                {busy && <Loader2 size={15} className="animate-spin" />} {v.cta}
              </button>
              <button type="button" onClick={() => close(false)} data-testid="lead-no-thanks"
                className="w-full text-xs text-[#51637a] hover:text-[#0d2f4d] transition-colors">
                No thanks
              </button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
